import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { IconPencil } from '@tabler/icons-react';
import * as Sentry from '@sentry/react';

import {
  recurringReportSortBy,
  reportStatus,
  reportStatusLabels,
  reportTypes,
  sortDirections,
} from '../core/constants';
import ReportsTable from '../components/reports/ReportsTable';
import AlertMessage from '../components/alerts/AlertMessage';
import appSettings from '../app-settings';
import { sortReports } from '../core/utils';
import Loader from '../components/loader/Loader';
import Button from '../components/buttons/Button';
import Filters from '../components/reports/Filters';
import Sorters from '../components/tables/Sorters';
import Chip from '../components/chip/Chip';

const REPORTS_URL = `${appSettings.baseUrl}/reports`;

const filterOptions = [
  { label: reportStatusLabels[reportStatus.scheduled], value: reportStatus.scheduled },
  { label: reportStatusLabels[reportStatus.stage0], value: reportStatus.stage0 },
  { label: reportStatusLabels[reportStatus.stage1], value: reportStatus.stage1 },
  { label: reportStatusLabels[reportStatus.stage2], value: reportStatus.stage2 },
  { label: reportStatusLabels[reportStatus.stage3], value: reportStatus.stage3 },
];

const sortingOptions = [
  { label: 'Date', value: recurringReportSortBy.sendAt },
  { label: 'Interactions', value: recurringReportSortBy.interaction },
  { label: 'Size', value: recurringReportSortBy.totalContacts },
];

const excludedColumnKeys = [
  'type',
  'uniqueInteractions',
  'donations',
  'amountRaised',
  'uniqueClicks',
  'uniqueOpens',
  'click',
  'open',
  'delivered',
  'deferred',
  'dropped',
  'bounce',
  'spamreport',
  'socialImpressions',
  'socialInteractions',
  'createdAt',
  'actions',
];

const WelcomeSeries = () => {
  const navigate = useNavigate();
  const [error, setError] = useState(false);
  const [campaignReports, setCampaignReports] = useState([]);
  const [sendStatus, setSendStatus] = useState('');
  const [loading, setLoading] = useState(false);
  const [sortBy, setSortBy] = useState(recurringReportSortBy.sendAt);
  const [sortDirection, setSortDirection] = useState(sortDirections.desc);

  const getCampaignReports = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(REPORTS_URL, { params: { type: reportTypes.recurring } });
      setCampaignReports(res.data);
    } catch (err) {
      Sentry.captureException(err);
      setError(err?.message || true);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    getCampaignReports();
  }, [getCampaignReports]);

  const data = useMemo(() => {
    let filteredData = campaignReports;
    if (sendStatus) {
      filteredData = filteredData.filter((re) => {
        if (sendStatus === reportStatus.stage3) {
          // "stage3" and "sent" both mean "completed"
          return [reportStatus.stage3, reportStatus.sent].includes(re.sendStatus);
        }
        return re.sendStatus === sendStatus;
      });
    }
    if (sortBy) {
      filteredData = sortReports(filteredData, sortBy, sortDirection);
    }
    return filteredData;
  }, [campaignReports, sendStatus, sortBy, sortDirection]);

  return (
    <div className="w-full space-y-6">
      <div className="space-y-4">
        <div className="flex justify-between space-x-3">
          <h1 className="text-h3">Welcome Series</h1>

          <div className="flex items-center space-x-3">
            <Sorters
              options={sortingOptions}
              sortBy={sortBy}
              setSortBy={setSortBy}
              sortDirection={sortDirection}
              setSortDirection={setSortDirection}
            />

            <Filters
              key={sendStatus}
              initialStatus={sendStatus}
              options={filterOptions}
              onSelect={(status) => setSendStatus(status)}
              active={!!sendStatus}
            />

            <Button
              title="Edit Welcome Series"
              LeftIcon={IconPencil}
              onClick={() => navigate('/welcome-series/edit')}
            />
          </div>
        </div>

        {!!sendStatus && (
          <div className="flex flex-wrap items-center space-x-2">
            <Chip
              label={reportStatusLabels[sendStatus]}
              onDelete={() => setSendStatus('')}
              className="border-0 bg-white-100 px-[10px] py-[5px] !text-gray-800"
              iconClassName="text-gray-400"
            />
          </div>
        )}
      </div>

      <div className="relative">
        <ReportsTable data={data} excludedColumnKeys={excludedColumnKeys} />

        {loading && <Loader />}
      </div>

      <AlertMessage
        open={!!error}
        message={typeof error === 'string' ? error : 'Oops, something went wrong!'}
        onClose={() => setError(false)}
        severity="error"
      />
    </div>
  );
};

export default WelcomeSeries;
